import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ChevronRight, MapPin, ArrowRight, Loader2, AlertCircle,
  Check, Clock, Users, Calendar, Star, ShoppingCart, Plus,
  ChevronLeft, ChevronDown, X
} from 'lucide-react';
import SEO from '../components/SEO';
import { cityName, categoryName, subCategoryName, cityState, cityWithState } from '../utils/seoFormat';
import { breadcrumbLD, serviceLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING_MODEL_LABELS = {
  flat_rate: 'Flat Rate',
  per_hour: 'Per Hour',
  per_person: 'Per Person',
  per_hour_per_person: 'Per Hour/Person'
};

export default function ProductDetailPage() {
  const { city, category, subCategory, slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  // Quantity controls — populated only for relevant pricing models
  const [hours, setHours] = useState(4);
  const [guestCount, setGuestCount] = useState(50);

  useEffect(() => {
    fetchProduct();
  }, [city, category, subCategory, slug]);

  // Initialize sensible defaults once product loads
  useEffect(() => {
    if (!product) return;
    setHours(product.min_hours || product.default_hours || 4);
    setGuestCount(product.min_guests || 50);
  }, [product]);

  const fetchProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/listings/${city}/${category}/${subCategory}/${slug}`);
      setProduct(response.data);
      
      // Set default options
      const defaults = {};
      response.data.option_groups?.forEach(og => {
        const defaultChoices = og.choices?.filter(c => c.is_default).map(c => c.choice_id) || [];
        if (defaultChoices.length > 0) {
          defaults[og.option_id] = og.multiple_select ? defaultChoices : defaultChoices[0];
        }
      });
      setSelectedOptions(defaults);
    } catch (err) {
      console.error('Failed to fetch product:', err);
      setError(err.response?.data?.detail || 'Product not found');
    } finally {
      setLoading(false);
    }
  };

  // Centralized customer-facing unit price.
  // Falls back from `customer_price` → `base_price * (1 + customer_markup_rate)` → `base_price`.
  const customerUnitPrice = (product) => {
    if (!product) return 0;
    if (typeof product.customer_price === 'number' && product.customer_price > 0) {
      return product.customer_price;
    }
    const base = Number(product.base_price) || 0;
    const markupRate = typeof product.customer_markup_rate === 'number' ? product.customer_markup_rate : 0;
    return base * (1 + markupRate);
  };

  const formatPrice = (product) => {
    if (!product) return '';
    const { pricing_model } = product;
    const cust = customerUnitPrice(product);
    const display = (typeof cust === 'number' && !Number.isNaN(cust)) ? cust.toFixed(2) : product.base_price;
    switch (pricing_model) {
      case 'flat_rate': return `$${display}`;
      case 'per_hour': return `$${display}/hr`;
      case 'per_person': return `$${display}/person`;
      case 'per_hour_per_person': return `$${display}/hr/person`;
      default: return `$${display}`;
    }
  };

  const handleOptionChange = (optionId, choiceId, isMultiple) => {
    setSelectedOptions(prev => {
      if (isMultiple) {
        const current = prev[optionId] || [];
        if (current.includes(choiceId)) {
          return { ...prev, [optionId]: current.filter(id => id !== choiceId) };
        } else {
          return { ...prev, [optionId]: [...current, choiceId] };
        }
      } else {
        return { ...prev, [optionId]: choiceId };
      }
    });
  };

  const calculateOptionsTotal = () => {
    let total = 0;
    Object.entries(selectedOptions).forEach(([optionId, choiceIds]) => {
      const optionGroup = product?.option_groups?.find(og => og.option_id === optionId);
      if (optionGroup) {
        const ids = Array.isArray(choiceIds) ? choiceIds : [choiceIds];
        ids.forEach(choiceId => {
          const choice = optionGroup.choices?.find(c => c.choice_id === choiceId);
          if (choice?.price_modifier) {
            total += choice.price_modifier;
          }
        });
      }
    });
    return total;
  };

  const isPerHour = product?.pricing_model === 'per_hour' || product?.pricing_model === 'per_hour_per_person';
  const isPerPerson = product?.pricing_model === 'per_person' || product?.pricing_model === 'per_hour_per_person';

  const computeLineTotal = () => {
    if (!product) return 0;
    const base = customerUnitPrice(product);
    const opts = calculateOptionsTotal();
    const h = isPerHour ? Math.max(1, Number(hours) || 1) : 1;
    const g = isPerPerson ? Math.max(1, Number(guestCount) || 1) : 1;
    return (base + opts) * h * g;
  };

  const handleAddToEvent = () => {
    if (!product) return;
    // Persist directly to the cart in localStorage so the planning hub
    // picks it up regardless of whether event details are filled.
    let existing = [];
    try {
      const raw = localStorage.getItem('eventism_cart');
      existing = raw ? JSON.parse(raw) : [];
    } catch { existing = []; }

    const idx = existing.findIndex(it => it.service_id === product.service_id);
    const cartItem = {
      ...product,
      quantity: 1,
      selectedOptions,
      guestCount: isPerPerson ? Math.max(1, Number(guestCount) || 1) : null,
      hours: isPerHour ? Math.max(1, Number(hours) || 1) : null,
      isDropoff: false,
    };
    if (idx >= 0) {
      existing[idx] = { ...existing[idx], ...cartItem, quantity: existing[idx].quantity + 1 };
    } else {
      existing.push(cartItem);
    }
    try {
      localStorage.setItem('eventism_cart', JSON.stringify(existing));
    } catch {
      toast.error('Unable to save to cart — please disable private/incognito mode.');
      return;
    }
    toast.success(`${product.name} added to cart`);
    navigate('/plan-event');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Service Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || 'This service does not exist'}</p>
            <Button onClick={() => navigate(`/suppliers/${city}/${category}`)}>
              Browse {categoryName(category) || 'Services'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cityDisplay = cityName(product.city || city);
  const state = cityState(product.city || city);
  const categoryLabel = categoryName(category);
  const subCategoryLabel = subCategoryName(subCategory);
  const galleryImages = product.gallery_images?.length > 0 
    ? product.gallery_images 
    : product.image_url ? [product.image_url] : [];
  const optionsTotal = calculateOptionsTotal();

  // ---- SEO -----------------------------------------------------------------
  const locLabel = state ? `${cityDisplay}, ${state}` : cityDisplay;
  const seoTitle = `${product.name} — ${subCategoryLabel} in ${cityDisplay}`;
  const seoDescription = (
    product.short_description ||
    product.description ||
    `${product.name} — book ${subCategoryLabel.toLowerCase()} for your ${locLabel} event. Transparent pricing, escrow-backed booking, instant availability.`
  ).slice(0, 300);
  const seoKeywords = [
    product.name,
    `${product.name} ${cityDisplay}`,
    `${subCategoryLabel} ${cityDisplay}`,
    `${categoryLabel.toLowerCase()} ${cityDisplay}`,
    `${cityDisplay} ${categoryLabel.toLowerCase()}`,
    `event ${subCategoryLabel.toLowerCase()} ${cityDisplay}`,
    `book ${subCategoryLabel.toLowerCase()} ${cityDisplay}`,
    `${state} ${categoryLabel.toLowerCase()}`,
    `Eventism ${cityDisplay}`,
    `${cityDisplay} event planning`,
    `${cityDisplay} party ${subCategoryLabel.toLowerCase()}`,
  ];
  const breadcrumbs = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: cityDisplay, url: `/suppliers/${city}` },
    { name: categoryLabel, url: `/suppliers/${city}/${category}` },
    { name: subCategoryLabel, url: `/suppliers/${city}/${category}/${subCategory}` },
    { name: product.name, url: `/suppliers/${city}/${category}/${subCategory}/${slug}` },
  ]);
  const seoLD = [serviceLD(product, locLabel), breadcrumbs];

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={product.image_url || galleryImages[0]}
        type="product"
        structuredData={seoLD}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/suppliers/${city}`} className="hover:text-foreground">{cityDisplay}</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/suppliers/${city}/${category}`} className="hover:text-foreground">{categoryLabel}</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/suppliers/${city}/${category}/${subCategory}`} className="hover:text-foreground">{subCategoryLabel}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground line-clamp-1">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Images */}
          <div>
            {/* Main Image */}
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted mb-4">
              {galleryImages.length > 0 ? (
                <img 
                  src={galleryImages[selectedImage]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No image available
                </div>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {galleryImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      selectedImage === idx ? 'border-indigo-500' : 'border-transparent hover:border-muted-foreground'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Details */}
          <div>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{categoryLabel}</Badge>
                <Badge variant="outline">{subCategoryLabel}</Badge>
                {product.is_featured && <Badge className="bg-amber-500">Featured</Badge>}
              </div>
              <h1 className="text-3xl font-bold mb-2" data-testid="product-title">{product.name}</h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{locLabel}</span>
              </div>
            </div>

            {/* Price */}
            <div className="mb-6 p-4 bg-slate-50 dark:bg-muted rounded-xl">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-indigo-500">{formatPrice(product)}</span>
                <span className="text-muted-foreground">{PRICING_MODEL_LABELS[product.pricing_model]}</span>
              </div>
              {optionsTotal > 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  + ${optionsTotal.toFixed(2)} in selected options
                </p>
              )}
              {product.min_guests && product.max_guests && (
                <p className="text-sm text-muted-foreground mt-2">
                  <Users className="h-4 w-4 inline mr-1" />
                  {product.min_guests} - {product.max_guests} guests
                </p>
              )}
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="w-full">
                <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
                <TabsTrigger value="included" className="flex-1">What's Included</TabsTrigger>
                <TabsTrigger value="requirements" className="flex-1">Requirements</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <h4 className="font-semibold mb-2">What to Expect</h4>
                  <p className="text-muted-foreground">
                    {product.what_to_expect || product.description || 'Contact us for more details about this service.'}
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="included" className="mt-4">
                {product.whats_included?.length > 0 ? (
                  <ul className="space-y-2">
                    {product.whats_included.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">Contact us for details on what's included.</p>
                )}
              </TabsContent>

              <TabsContent value="requirements" className="mt-4">
                {product.host_requirements?.length > 0 ? (
                  <ul className="space-y-2">
                    {product.host_requirements.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">No special requirements from the host.</p>
                )}
              </TabsContent>
            </Tabs>

            {/* Options */}
            {product.option_groups?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-4">Customize Your Service</h3>
                <div className="space-y-6">
                  {product.option_groups.map(optionGroup => (
                    <div key={optionGroup.option_id} className="border rounded-lg p-4">
                      <div className="mb-3">
                        <h4 className="font-medium flex items-center gap-2">
                          {optionGroup.name}
                          {optionGroup.required && (
                            <Badge variant="destructive" className="text-xs">Required</Badge>
                          )}
                        </h4>
                        {optionGroup.description && (
                          <p className="text-sm text-muted-foreground">{optionGroup.description}</p>
                        )}
                      </div>

                      {optionGroup.multiple_select ? (
                        <div className="space-y-2">
                          {optionGroup.choices?.map(choice => (
                            <label 
                              key={choice.choice_id} 
                              className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                            >
                              <Checkbox
                                checked={(selectedOptions[optionGroup.option_id] || []).includes(choice.choice_id)}
                                onCheckedChange={() => handleOptionChange(optionGroup.option_id, choice.choice_id, true)}
                              />
                              <div className="flex-1">
                                <span className="font-medium">{choice.name}</span>
                                {choice.description && (
                                  <p className="text-sm text-muted-foreground">{choice.description}</p>
                                )}
                              </div>
                              <span className={choice.price_modifier > 0 || choice.flat_price > 0 ? 'text-indigo-500 font-medium' : 'text-muted-foreground'}>
                                {choice.flat_price > 0
                                  ? `$${choice.flat_price.toFixed(2)}`
                                  : choice.price_modifier > 0 ? `+$${choice.price_modifier.toFixed(2)}` : 'Included'}
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <RadioGroup
                          value={selectedOptions[optionGroup.option_id] || ''}
                          onValueChange={(value) => handleOptionChange(optionGroup.option_id, value, false)}
                        >
                          {optionGroup.choices?.map(choice => (
                            <label 
                              key={choice.choice_id} 
                              className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                            >
                              <RadioGroupItem value={choice.choice_id} />
                              <div className="flex-1">
                                <span className="font-medium">{choice.name}</span>
                                {choice.description && (
                                  <p className="text-sm text-muted-foreground">{choice.description}</p>
                                )}
                              </div>
                              <span className={choice.price_modifier !== 0 || choice.flat_price > 0 ? 'text-indigo-500 font-medium' : 'text-muted-foreground'}>
                                {choice.flat_price > 0
                                  ? `$${choice.flat_price.toFixed(2)}`
                                  : choice.price_modifier > 0 
                                    ? `+$${choice.price_modifier.toFixed(2)}` 
                                    : choice.price_modifier < 0 
                                      ? `-$${Math.abs(choice.price_modifier).toFixed(2)}` 
                                      : 'Included'}
                              </span>
                            </label>
                          ))}
                        </RadioGroup>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity controls — only shown for time/headcount-based pricing */}
            {(isPerHour || isPerPerson) && (
              <div className="mb-6 p-4 rounded-xl border border-indigo-500/20 bg-indigo-500/5" data-testid="qty-controls">
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-indigo-500">Customize</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {isPerHour && (
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1.5 flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        How many hours?
                      </label>
                      <div className="flex items-center gap-2">
                        <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => {
                          const minH = Number(product.min_hours) || 1;
                          setHours(h => Math.max(minH, Number(h) - 1));
                        }} data-testid="hours-decrement">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <input
                          type="number"
                          min={product.min_hours || 1}
                          max={product.max_hours || undefined}
                          value={hours}
                          onChange={e => setHours(e.target.value)}
                          onBlur={e => {
                            const v = Number(e.target.value) || 0;
                            const minH = Number(product.min_hours) || 1;
                            const maxH = Number(product.max_hours) || Infinity;
                            setHours(Math.min(maxH, Math.max(minH, v)));
                          }}
                          className="flex-1 h-9 px-3 rounded-md border bg-background text-center font-semibold"
                          data-testid="hours-input"
                        />
                        <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => {
                          const maxH = Number(product.max_hours) || Infinity;
                          setHours(h => Math.min(maxH, Number(h) + 1));
                        }} data-testid="hours-increment">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                      {(product.min_hours || product.max_hours) && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {product.min_hours && product.max_hours
                            ? `Allowed: ${product.min_hours}–${product.max_hours} hours`
                            : product.min_hours
                              ? `Min ${product.min_hours} hours`
                              : `Max ${product.max_hours} hours`}
                        </p>
                      )}
                    </div>
                  )}
                  {isPerPerson && (
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1.5 flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        How many people?
                      </label>
                      <div className="flex items-center gap-2">
                        <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => {
                          const minG = Number(product.min_guests) || 1;
                          setGuestCount(g => Math.max(minG, Number(g) - 5));
                        }} data-testid="guests-decrement">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <input
                          type="number"
                          min={product.min_guests || 1}
                          max={product.max_guests || undefined}
                          value={guestCount}
                          onChange={e => setGuestCount(e.target.value)}
                          onBlur={e => {
                            const v = Number(e.target.value) || 0;
                            const minG = Number(product.min_guests) || 1;
                            const maxG = Number(product.max_guests) || Infinity;
                            setGuestCount(Math.min(maxG, Math.max(minG, v)));
                          }}
                          className="flex-1 h-9 px-3 rounded-md border bg-background text-center font-semibold"
                          data-testid="guests-input"
                        />
                        <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => {
                          const maxG = Number(product.max_guests) || Infinity;
                          setGuestCount(g => Math.min(maxG, Number(g) + 5));
                        }} data-testid="guests-increment">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                      {(product.min_guests || product.max_guests) && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {product.min_guests && product.max_guests
                            ? `Allowed: ${product.min_guests}–${product.max_guests} guests`
                            : product.min_guests
                              ? `Min ${product.min_guests} guests`
                              : `Max ${product.max_guests} guests`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-4 flex items-baseline justify-between border-t border-indigo-500/20 pt-3">
                  <span className="text-sm text-muted-foreground">Line total</span>
                  <span className="text-2xl font-bold text-indigo-500" data-testid="line-total">
                    ${computeLineTotal().toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="flex gap-3">
              <Button 
                size="lg" 
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                onClick={handleAddToEvent}
                data-testid="add-to-event-btn"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Cart
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/plan-event')}
              >
                Plan Event
              </Button>
            </div>

            {/* Back link */}
            <div className="mt-6">
              <Button 
                variant="ghost" 
                onClick={() => navigate(`/suppliers/${city}/${category}/${subCategory}`)}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to {subCategoryLabel}
              </Button>
            </div>
          </div>
        </div>

        {/* Related Services section could go here */}
      </div>
    </div>
  );
}
